import classNames from 'classnames';
import React, { useId } from 'react';
import { notNil } from '../../core/helpers/is-nil';
import { useAccentBgContext } from '../accent-bg-context/accent-bg-context';
import { FormHelperText } from '../form-helper-text/form-helper-text';
import { FormInlineNotification, InlineNotification } from '../form-inline-notification/form-inline-notification';
import { FormLabel } from '../form-label/form-label';
import { Spacer } from '../spacer/spacer';
import { Typography } from '../typography/typography';
import styles from './input.module.scss';

type Props = {
  type: 'text' | 'email' | 'password' | 'number';
  className?: string;
  label?: string;
  mandatory?: boolean;
  placeholder?: string;
  inlineNotification?: InlineNotification;
  helperMsg?: string;
  isDirty?: boolean;
  unit?: string;
} & React.InputHTMLAttributes<HTMLInputElement>;

export const Input: React.FC<Props> = (props) => {
  const {
    className,
    type,
    label,
    mandatory,
    placeholder,
    inlineNotification,
    disabled,
    helperMsg,
    isDirty = false,
    onChange,
    ...rest
  } = props;

  const { isAccentBackground } = useAccentBgContext();
  const inputId = useId();
  const helperId = useId();

  const hasLabel = notNil(label);
  const hasHelper = notNil(helperMsg);
  const hasInlineNotification = notNil(inlineNotification);
  const hasError = notNil(inlineNotification) && inlineNotification.type === 'error';
  const hasUnit = notNil(props.unit);

  return (
    <div className={styles.root}>
      {hasLabel ? (
        <FormLabel disabled={disabled} htmlFor={inputId} mandatory={mandatory}>
          {label}
        </FormLabel>
      ) : null}
      <div className={styles.innerContainer}>
        <span
          className={classNames(styles.inputContainer, {
            [styles.isDirty]: isDirty,
            [styles.disabled]: disabled,
            [styles.withUnit]: hasUnit,
            [styles.accent]: isAccentBackground,
          })}
        >
          <input
            aria-describedby={hasHelper ? helperId : undefined}
            aria-invalid={hasError}
            className={classNames(styles.input, className)}
            disabled={disabled}
            id={inputId}
            placeholder={placeholder}
            type={type}
            onChange={onChange}
            {...rest}
          />
          {hasUnit ? (
            <Typography className={styles.unit} color={isAccentBackground ? 'white' : 'black'} variant="maintext">
              {props.unit}
            </Typography>
          ) : null}
        </span>
        {hasInlineNotification ? (
          <FormInlineNotification type={inlineNotification.type}>{inlineNotification.message}</FormInlineNotification>
        ) : null}
        {hasHelper ? (
          <>
            <Spacer size="03" />
            <FormHelperText disabled={disabled} id={helperId}>
              {helperMsg}
            </FormHelperText>
          </>
        ) : null}
      </div>
    </div>
  );
};
